/**
 * Timeline Context Types
 *
 * State management types for synchronized zoom/pan across timeline components.
 */

import type { TimelineTransform, TimelineDomain, ZoomConstraints } from '../types'

/**
 * State managed by the timeline context
 */
export interface TimelineContextState {
  /** Current zoom/pan transform */
  transform: TimelineTransform

  /** Computed domain bounds */
  domain: TimelineDomain

  /** Zoom constraints */
  constraints: ZoomConstraints

  /** Container width in pixels */
  containerWidth: number

  /** Whether currently animating */
  isAnimating: boolean
}

/**
 * Actions for the timeline reducer
 */
export type TimelineAction =
  | { type: 'SET_TRANSFORM'; payload: TimelineTransform }
  | { type: 'ZOOM_IN' }
  | { type: 'ZOOM_OUT' }
  | { type: 'RESET' }
  | { type: 'PAN'; payload: number }
  | { type: 'SET_DOMAIN'; payload: TimelineDomain }
  | { type: 'SET_CONTAINER_WIDTH'; payload: number }
  | { type: 'SET_ANIMATING'; payload: boolean }
  | { type: 'SET_CONSTRAINTS'; payload: Partial<ZoomConstraints> }

/**
 * Context value provided to consuming components
 */
export interface TimelineContextValue {
  /** Current state */
  state: TimelineContextState

  /** Convert a date to X pixel position */
  dateToX: (date: Date | string | number | null) => number

  /** Convert an X pixel position to a Date */
  xToDate: (x: number) => Date

  /** Get pixel bounds for a segment given its date range */
  getSegmentBounds: (from: string | null, to: string | null) => { x: number; width: number }

  /** Zoom in by one step */
  zoomIn: () => void

  /** Zoom out by one step */
  zoomOut: () => void

  /** Reset to initial zoom/pan state */
  reset: () => void

  /** Set transform directly */
  setTransform: (transform: TimelineTransform) => void

  /** Pan by a delta X amount */
  pan: (deltaX: number) => void

  /** Update the domain */
  setDomain: (domain: TimelineDomain) => void

  /** Update container width */
  setContainerWidth: (width: number) => void

  /** Check if can zoom in further */
  canZoomIn: boolean

  /** Check if can zoom out further */
  canZoomOut: boolean
}

// Duration constants for zoom constraints
const ONE_HOUR = 60 * 60 * 1000
const ONE_DAY = 24 * ONE_HOUR
const ONE_YEAR = 365 * ONE_DAY

/**
 * Default zoom constraints using duration-based values.
 * minScale/maxScale are fallbacks; actual values calculated from domain span.
 */
export const DEFAULT_ZOOM_CONSTRAINTS: ZoomConstraints = {
  minScale: 0.1,  // Fallback if duration calc fails
  maxScale: 100,  // Fallback if duration calc fails
  zoomStep: 1.5,
  minVisibleDuration: ONE_DAY,      // Max zoom in: 24 hours visible
  maxVisibleDuration: 3 * ONE_YEAR, // Max zoom out: 3 years visible
}

/**
 * Default transform (no zoom, no pan)
 */
export const DEFAULT_TRANSFORM: TimelineTransform = {
  x: 0,
  y: 0,
  k: 1,
}
